import { ActivatedRoute, Router } from '@angular/router';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Company, Invitation, Profile } from 'app/models';
import { AuthService, CompanyService, InvitationsService, UsersService } from 'app/services';
import { takeUntil } from 'rxjs/operators';

import { combineLatest, Subject } from 'rxjs';
import { BranchUsers } from 'app/models/branchUsers';
import { Subscription } from 'app/models/subscription';
import { SubscriptionService } from 'app/services/subscription-service/subscription.service';

@Component({
  standalone: false,
  selector: 'app-company-detail',
  templateUrl: './company-detail.component.html',
  styleUrls: ['./company-detail.component.scss'],
})
export class CompanyDetailComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private onDestroy$ = new Subject<void>();
  subscriptionCount: number = 0;
  companyInitials: string;
  readonly tabs = [
    { name: 'Overview', id: 0 },
    { name: 'Branches', id: 1 },
    { name: 'Users', id: 2 },
    { name: 'Invitations', id: 3 },
    { name: 'Subscriptions', id: 4}
  ];
  selectedTab;
  branches: Company[];
  invitations: Invitation[];
  accountId: string;
  company: Company;
  error: any;
  usersProfile: Subject<Profile[]> = new Subject();
  companyBranches: Subject<Company[]> = new Subject();
  blockedUser: Subject<number> = new Subject();
  activeUsers: Profile[];
  invitationSub: Subject<Invitation[]> = new Subject();
  invitationsError: string;
  userHasAccess: boolean = false;
  subscriptions: Subscription[] = [];

  constructor(
    public userService: UsersService,
    private route: ActivatedRoute,
    private router: Router,
    private companyServ: CompanyService,
    private changeDetect: ChangeDetectorRef,
    private invitationsService: InvitationsService,
    private readonly authService: AuthService,
    private readonly subscriptionService: SubscriptionService
  ) {}

  ngOnInit() {
    this.subscribeInvitations();
    this.userService.loading = true;
    this.route.queryParamMap.subscribe(r => {
      const index = Number(r.get('index'));
      if (index) {
        this.selectedTab = this.tabs.find((e) => e.id === index);
      } else {
        this.selectedTab = this.tabs[0];
      }
    });

    this.subscribeToUsersAndCompany();
    this.accountId = this.route.snapshot.params['id'];
    
    if (this.accountId) {
      this.companyServ.getCompany(this.accountId);
      this.companyServ.getCompanyBranchesWithUsers(this.accountId);
      this.userService.getUsersByCompanyId(this.accountId, true);
      this.invitationsService.getCompanyInvitations(this.accountId);
      this.loadSubscriptions();
    }

    this.authService.user$.subscribe(user => {
        if (user) {
          this.userHasAccess = user.isAppManager || user.isCompanyManager;
        }
      });  
  }

  loadSubscriptions() {
    this.subscriptionService.getSubscriptions(this.accountId);
    this.subscriptionService.subscriptions$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (subscriptions: Subscription[]) => {
          this.subscriptions = subscriptions;
        }
      });
  }

  subscribeInvitations() {
    this.invitationsService.invitations$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (invitations) => {
          if (invitations) {
            invitations.sort((a, b) =>
              a.email.toLowerCase() > b.email.toLowerCase() ? 1 : -1
            );
            invitations.forEach((invite) => {
              invite['status'] = 'Pending';
            });
            this.userService.loading = false;
            this.invitations = invitations;
            this.invitationSub.next(this.invitations);
          }
        },
        (error) => {
          this.invitationsError = error;
          this.userService.loading = false;
        }
      );
  }

  updateTab(id) {
    this.router.navigate([], {
      queryParams: {
        index: id,
      },
    });
    this.changeDetect.detectChanges();
  }

  ngAfterViewInit() {
    this.changeDetect.detectChanges();
  }
  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  goToUser(event) {
    if (event) {
      const usertab = 2;
      this.onChangeTab(usertab);
      this.changeDetect.detectChanges();
    }
  }

  goToSubscriptionTab(event: boolean) {
    if (event) {
      this.onChangeTab(4);
      this.changeDetect.detectChanges();
    }
  }

  subscribeToUsersAndCompany(): void {
    const branchesUsers$ = this.companyServ.branchesUsers$;
    const company$ = this.companyServ.company$;
    const users$ = this.userService.companyUsers$;
  
    combineLatest([branchesUsers$, company$, users$])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([branchesUsers, company, users]) => {
        if (company && branchesUsers && users) {
          company.branches = this.parseLocation(company.branches);
          this.branches = company.branches;
          this.company = company;
          this._parseName();
  
          users.forEach(user => {
            const assignedBranchIds = branchesUsers
              .filter(branch => branch.users.map(x=>x.userId).includes(user.id))
              .map(branch => branch.branchId);
              user.branchAssignments = assignedBranchIds;
          });
          this.activeUsers = users;
          this.setBranchAdmins(company.branches, branchesUsers);
          this.companyBranches.next(this.branches);
          this.usersProfile.next(this.activeUsers);
        }
        this.userService.loading = false;
      });
  
    this.companyServ.error$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((error) => {
        this.userService.loading = false;
        this.error = error;
      });
  }

  private setBranchAdmins(companyBranches: Company[], branchesUsers: BranchUsers[]) {
    companyBranches.forEach(companyBranch => {
      const branchUsers = branchesUsers?.find((b) => b.branchId === companyBranch.accountId);
      companyBranch.branchAdmins = branchUsers?.users?.filter(user => user.roles.includes('admin') && !this.activeUsers?.find(x => x.id === user.userId)?.isCompanyAdmin)?.map(user => user.userId) ?? [];
    });
  }

  private _parseName() {
    if (!this.company.name) {
      return;
    }
    this.companyInitials = `${this.company.name[0].toUpperCase()}${this.company.name[
      this.company.name.length - 1
    ].toUpperCase()}`;
  }

  parseLocation(companiesBranches) {
    companiesBranches.forEach((compBranch) => {
      compBranch.location = compBranch.city + ', ' + compBranch.state;
    });
    return companiesBranches;
  }

  onChangeTab(tabIndex) {
    this.selectedTab = this.tabs.find(t => t.id === tabIndex);
    this.updateTab(tabIndex);
    if (this.selectedTab.id === 1 || this.selectedTab.id === 3) {
      this.companyBranches.next(this.branches);
    } else if (
      this.selectedTab.id === 2 ||
      this.selectedTab.id === 3 ||
      this.selectedTab.id === 0
    ) {
      this.usersProfile.next(this.activeUsers);
    }

    if (this.selectedTab.id === 3) {
      this.invitationSub.next(this.invitations);
      this.usersProfile.next(this.activeUsers);
    }
  }
}
